import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getDefaultStore } from "jotai";
import { api } from "jiffy-api";
import { objNamesToSnakeCase, objNamesToCamelCase } from "common/utils/helpers/cases";
import type {
  UpdateDesignProjectRequest,
  UpdateDesignProjectResponse
} from "@design-lab/shared/types";
import { addToCartLoadingAtom } from "@design-lab/shared/state";
import {
  designProjectPlacementIdsAtom,
  type DesignLabPlacementKey
} from "@design-lab/entities/pinning";

export function useUpdateDesignProject() {
  const queryClient = useQueryClient();

  return useMutation<UpdateDesignProjectResponse, Error, UpdateDesignProjectRequest>({
    mutationFn: async ({ id, payload }: UpdateDesignProjectRequest) => {
      const snakeCasePayload = objNamesToSnakeCase(payload);
      try {
        const response = await api.designLab.updateDesignProject({ id }, snakeCasePayload);
        const parsedResponse = objNamesToCamelCase(response) as UpdateDesignProjectResponse;

        const placements = parsedResponse?.designProject?.placements;
        if (placements) {
          const { front } = placements;
          const { back } = placements;

          const store = getDefaultStore();
          const frontPosition = String(front?.position || "");
          const isLeftChest =
            frontPosition === "front_left_chest" || frontPosition === "frontLeftChest";
          const frontId = front?.id ? String(front.id) : null;
          const backId = back?.id ? String(back.id) : null;

          const nextPlacementIds: Record<DesignLabPlacementKey, string | null> = {
            front: isLeftChest ? null : frontId,
            back: backId,
            frontLeftChest: isLeftChest ? frontId : null
          };

          store.set(designProjectPlacementIdsAtom, nextPlacementIds);
        }

        return parsedResponse;
      } catch (e: any) {
        const errorResponse = e?.response;

        // If project not found with redirect instruction, redirect silently (without Honeybadger)
        if (errorResponse?.error === "project_not_found" && errorResponse?.redirect) {
          const store = getDefaultStore();
          const isAddToCartInFlight = store.get(addToCartLoadingAtom);

          if (!isAddToCartInFlight) {
            window.location.href = errorResponse.redirect;
          }

          return new Promise(() => {
            // no ops
          });
        }

        window.Honeybadger?.notify("Failed to update design project", {
          params: {
            error: e instanceof Error ? e.message : String(e),
            error_object: e instanceof Error ? e.toString() : String(e),
            stack_trace: e instanceof Error ? e.stack : null,
            hook: "useUpdateDesignProject",
            id,
            payload
          }
        });
        const [firstMessage] = errorResponse?.messages ?? ["An unknown error occurred"];
        const error = new Error(firstMessage);
        throw error;
      }
    },
    onSuccess: (data: UpdateDesignProjectResponse) => {
      // Update the cache with the new data from the PUT response
      // This prevents any GET requests as long as the response is complete
      queryClient.setQueryData(["designProject"], data);
    }
  });
}
